import {
  Mail,
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import type { ComponentType, MouseEvent } from 'react';
import { Card, Badge } from '../../ui';
import { Can } from '../../app/Can';
import { formatDateBR } from '../../utils/format';

type CampaignStatus = 'DRAFT' | 'QUEUED' | 'SENDING' | 'SENT' | 'FAILED';

const STATUS: Record<
  CampaignStatus,
  {
    label: string;
    tone: 'neutral' | 'warning' | 'info' | 'accent' | 'danger';
    icon: ComponentType<{ className?: string }>;
    spin?: boolean;
  }
> = {
  DRAFT: { label: 'Rascunho', tone: 'neutral', icon: Mail },
  QUEUED: { label: 'Na fila', tone: 'warning', icon: Clock },
  SENDING: { label: 'Enviando', tone: 'info', icon: Loader2, spin: true },
  SENT: { label: 'Enviado', tone: 'accent', icon: CheckCircle2 },
  FAILED: { label: 'Falhou', tone: 'danger', icon: XCircle },
};

interface CampaignCardProps {
  campaign: {
    id: number;
    name: string;
    status: string;
    totalRecipients?: number;
    totalSent?: number;
    totalFailed?: number;
    totalBounced?: number;
    template?: { name: string } | null;
    templateName?: string;
    createdAt: string;
    automation?: Partial<Record<string, number>> | null;
  };
  onClick: () => void;
  onDelete?: () => void;
}

function formatAutomation(automation?: Partial<Record<string, number>> | null): string | null {
  if (!automation) {
    return null;
  }
  const parts: string[] = [];
  const moved = automation.MOVED ?? 0;
  const noLead = automation.SKIPPED_NO_LEAD ?? 0;
  const orderRule = automation.SKIPPED_ORDER_RULE ?? 0;
  const noTarget = automation.SKIPPED_NO_TARGET_CONFIG ?? 0;
  const templateFlag = automation.SKIPPED_TEMPLATE_FLAG ?? 0;
  const failed = automation.FAILED ?? 0;
  if (moved > 0) {
    parts.push(`${moved} movido${moved !== 1 ? 's' : ''}`);
  }
  if (noLead > 0) {
    parts.push(`${noLead} sem lead`);
  }
  if (orderRule > 0) {
    parts.push(`${orderRule} fora de ordem`);
  }
  if (noTarget > 0) {
    parts.push(`${noTarget} sem alvo`);
  }
  if (templateFlag > 0) {
    parts.push(`${templateFlag} sem mover (template)`);
  }
  if (failed > 0) {
    parts.push(`${failed} erro${failed !== 1 ? 's' : ''}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

export function CampaignCard({ campaign, onClick, onDelete }: CampaignCardProps) {
  function handleDelete(e: MouseEvent) {
    e.stopPropagation();
    onDelete?.();
  }
  const status = STATUS[campaign.status as CampaignStatus] ?? STATUS.DRAFT;
  const Icon = status.icon;
  const total = campaign.totalRecipients ?? 0;
  const sent = campaign.totalSent ?? 0;
  const failed = campaign.totalFailed ?? 0;
  const bounced = campaign.totalBounced ?? 0;
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
  const templateName = campaign.template?.name ?? campaign.templateName ?? '—';
  const automationParts = formatAutomation(campaign.automation);

  return (
    <Card
      padding="md"
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-ink-primary truncate">{campaign.name || 'Sem nome'}</h3>
            <Badge tone={status.tone} size="sm">
              <Icon className={`w-3 h-3 mr-1 ${status.spin ? 'animate-spin' : ''}`} />
              {status.label}
            </Badge>
          </div>
          <p className="text-xs text-ink-tertiary mt-1">
            {templateName} · criada {formatDateBR(campaign.createdAt)}
          </p>
          {total > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-ink-secondary mb-1">
                <span>
                  {sent} de {total} enviado{total !== 1 ? 's' : ''}
                </span>
                <span className="font-mono">{pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-subtle overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
              </div>
              {failed > 0 && (
                <p className="text-xs text-danger mt-1">
                  {failed} falha{failed !== 1 ? 's' : ''}
                </p>
              )}
              {bounced > 0 && (
                <p className="text-xs text-warning mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {bounced} devolvido{bounced !== 1 ? 's' : ''} (bounce)
                </p>
              )}
              {automationParts && (
                <p className="text-xs text-ink-tertiary mt-1">automação · {automationParts}</p>
              )}
            </div>
          )}
        </div>
        <div className="flex items-start gap-1 shrink-0 mt-1">
          {onDelete && (
            <Can do="campaigns.delete">
              <button
                onClick={handleDelete}
                aria-label="Excluir campanha"
                title="Excluir campanha"
                className="p-1.5 rounded-md text-ink-tertiary hover:text-danger hover:bg-subtle transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </Can>
          )}
          <ChevronRight className="w-4 h-4 text-ink-tertiary" />
        </div>
      </div>
    </Card>
  );
}

export default CampaignCard;
